import React, { useState } from 'react';
import { ExternalLink, Zap, PackageOpen, ChevronUp } from 'lucide-react';
import ConsumptionPatternModal from './ConsumptionPatternModal';

const EMERGENCY_PREVIEW_COUNT = 3;

const ExceptionAlerts = ({ emergencyPurchases: emergencyPurchasesProp, excessConsumption: excessConsumptionProp }) => {
    const emergencyPurchases = emergencyPurchasesProp ?? [];
    const excessConsumption = excessConsumptionProp ?? [];
    const hasEmergency = emergencyPurchases.length > 0;
    const [emergencyExpanded, setEmergencyExpanded] = useState(false);
    const [consumptionModalOpen, setConsumptionModalOpen] = useState(false);
    const [selectedExcessItem, setSelectedExcessItem] = useState(null);

    const showViewAll = hasEmergency && emergencyPurchases.length > EMERGENCY_PREVIEW_COUNT;
    const visibleEmergency = emergencyExpanded
        ? emergencyPurchases
        : emergencyPurchases.slice(0, EMERGENCY_PREVIEW_COUNT);

    return (
        <div className="bg-white rounded-3xl p-6 shadow-sm border border-gray-100 flex flex-col h-full">
            <div className="flex justify-between items-center mb-6">
                <div className="flex items-center gap-2">
                    <Zap className="text-gray-600" size={20} fill="currentColor" />
                    <h2 className="text-xl font-bold text-gray-900">Risk Alerts</h2>
                    {hasEmergency && (
                        <span className="bg-red-100 text-red-600 text-xs font-bold px-2 py-0.5 rounded-full ml-1">
                            {emergencyPurchases.length} alert{emergencyPurchases.length !== 1 ? 's' : ''}
                        </span>
                    )}
                </div>
            </div>

            {/* Emergency Purchases */}
            <div className="mb-6">
                <div className="flex justify-between items-center mb-3">
                    <h3 className="font-semibold text-gray-800 text-lg">Emergency Purchases ({emergencyPurchases.length})</h3>
                    {showViewAll && (
                        <button
                            type="button"
                            onClick={() => setEmergencyExpanded((e) => !e)}
                            className="flex items-center gap-1 text-xs font-medium text-gray-500 border border-gray-200 rounded px-2 py-1 hover:bg-gray-50 transition-colors"
                        >
                            {emergencyExpanded ? (
                                <>
                                    <ChevronUp size={12} />
                                    View Less
                                </>
                            ) : (
                                <>
                                    <ExternalLink size={12} />
                                    View All
                                </>
                            )}
                        </button>
                    )}
                </div>

                {hasEmergency ? (
                    <div className="space-y-3">
                        {visibleEmergency.map((item) => (
                            <div key={item.item_id} className="bg-gray-50/50 rounded-xl p-4 border-l-4 border-l-red-500 flex justify-between items-start gap-4">
                                <div className="min-w-0 flex-1">
                                    <h4 className="font-bold text-gray-900">{item.itemName}</h4>
                                    <p className="text-sm text-gray-600 mt-1">
                                        Total Quantity: <span className="font-semibold">{item.totalQuantity ?? 0}</span>
                                        {item.unit && <span className="text-gray-500"> ({item.unit})</span>}
                                    </p>
                                </div>
                                <div className="text-red-500 font-bold text-base whitespace-nowrap">
                                    ₹ {(item.totalAmount ?? 0).toLocaleString('en-IN')}
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="flex flex-col items-center justify-center py-8 px-4 bg-gray-50/50 rounded-xl border border-gray-100 border-dashed">
                        <PackageOpen className="w-10 h-10 text-gray-300 mb-2" strokeWidth={1.25} />
                        <p className="text-sm font-medium text-gray-500">No emergency purchases</p>
                        <p className="text-xs text-gray-400 mt-0.5">No emergency purchases for the selected month.</p>
                    </div>
                )}
            </div>

            {/* Excess Consumption */}
            <div>
                <h3 className="font-semibold text-gray-800 text-lg mb-4">Excess Consumption (≥80% threshold)</h3>
                {excessConsumption.length > 0 ? (
                    <div className="space-y-3">
                        {excessConsumption.map((item) => (
                            <div key={item.taskId} className="bg-gray-50/50 rounded-xl p-4 border-l-4 border-l-yellow-400">
                                <div className="flex justify-between items-start mb-2">
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <h4 className="font-bold text-gray-900">{item.itemName}</h4>
                                            {/* <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-yellow-100 text-yellow-600">
                                                Excess
                                            </span> */}
                                        </div>
                                        <p className="text-sm text-gray-500 mt-0.5">Task: <span className="text-gray-700 font-medium">{item.taskName}</span></p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-8 mb-3 text-sm">
                                    <div className="flex items-center gap-1">
                                        <span className="text-gray-500">Estimated:</span>
                                        <span className="font-bold text-yellow-600">{item.estimated ?? 0}{item.unit ? ` (${item.unit})` : ''}</span>
                                    </div>
                                    <div className="flex items-center gap-1">
                                        <span className="text-gray-500">Actual:</span>
                                        <span className="font-bold text-yellow-600">{item.actual ?? 0}{item.unit ? ` (${item.unit})` : ''}</span>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setSelectedExcessItem(item);
                                        setConsumptionModalOpen(true);
                                    }}
                                    className="w-full flex items-center justify-center gap-2 text-xs font-medium text-gray-500 border border-gray-200 rounded-lg py-1.5 hover:bg-gray-100 transition-colors"
                                >
                                    <ExternalLink size={12} />
                                    Review Consumption Pattern
                                </button>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="flex flex-col items-center justify-center py-8 px-4 bg-gray-50/50 rounded-xl border border-gray-100 border-dashed">
                        <PackageOpen className="w-10 h-10 text-gray-300 mb-2" strokeWidth={1.25} />
                        <p className="text-sm font-medium text-gray-500">No excess consumption</p>
                        <p className="text-xs text-gray-400 mt-0.5">No excess consumption for the selected month.</p>
                    </div>
                )}
            </div>

            <ConsumptionPatternModal
                isOpen={consumptionModalOpen}
                onClose={() => {
                    setConsumptionModalOpen(false);
                    setSelectedExcessItem(null);
                }}
                data={selectedExcessItem}
            />
        </div>
    );
};

export default ExceptionAlerts;
